import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  CircleStop,
  Clapperboard,
  Puzzle,
  CloudUpload,
  Database,
  Mic2,
  MonitorUp,
  PictureInPicture2,
  Play,
  RefreshCw,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Volume2,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth, useCurrentCompany } from '../features/auth/AuthProvider';
import { useRecorder } from '../features/recorder/RecorderProvider';
import {
  formatRecorderDuration,
  RecorderFloatingControl,
} from '../features/recorder/RecorderFloatingControl';
import type {
  RecordingSegmentController,
  RecordingSegmentSnapshot,
} from '../features/recorder/RecordingSegmentController';
import type { LocalRecordingSession, LocalRecordingUpload, RecorderPhase } from '../features/recorder/types';
import { MAX_RECORDING_PAUSE_MS } from '../features/recorder/types';
import { startRecorderWithFloatingControl } from '../features/recorder/recorderLaunch';
import { RecordingStartGuide, useRecordingGuideHidden } from '../features/recorder/RecordingStartGuide';
import { deriveRecordingProgress, shouldHandOffToReview } from '../features/recorder/recordingProgress';
import { summarizeInFlight } from '../features/recorder/inFlightSessions';
import { confirmDiscardWalkthrough } from '../features/recorder/confirmDiscard';
import { PastWalkthroughs } from '../features/recorder/PastWalkthroughs';
import { LearnAboutRecordings } from '../features/recorder/LearnAboutRecordings';
import { keptFor, useVideoRetentionDays } from '../features/recorder/videoRetention';
import { WALKTHROUGH_PANEL_ID, WalkthroughTabs, type WalkthroughTab } from '../features/recorder/WalkthroughTabs';
import { useMicrophoneSwitch } from '../features/recorder/useMicrophoneSwitch';
import { PageHeader, pageBackground } from '../features/shell/PageHeader';
import { InfoTip } from '../features/reports/charts/HoverTip';
import { EXTENSION_INSTALL_PATH } from '@vd/types';
import styles from './RecordingsPage.module.css';
import { useTabTitle, useTabWorkspaceName } from '../lib/tabTitle';
import { When } from '../lib/When';

const ERROR_MESSAGES: Record<string, string> = {
  permission_denied: 'Chrome did not allow screen or microphone access. Check site permissions and try again.',
  device_unavailable: 'That microphone is no longer available. Choose another input and test it again.',
  device_busy: 'Another app is using the selected capture device. Close it there, then retry.',
  capture_cancelled: 'Screen sharing was cancelled. Nothing was recorded.',
  capture_failed: 'Capture could not start. Your local recovery data is still safe.',
  recovery_owner_mismatch: 'This recovery belongs to a different signed-in user.',
  recovery_unavailable: 'Local recovery storage is unavailable in this browser.',
  storage_full: 'Chrome could not save the latest chunk. Earlier chunks are still available for recovery.',
  recorder_failed: 'Chrome stopped the recorder unexpectedly. Saved chunks are available for recovery.',
  microphone_lost: 'The microphone disconnected, so recording stopped before it could continue without audio.',
  recording_duration_limit: 'This walkthrough reached the longest recording your plan allows and stopped cleanly. Its video and ticket markers are saved.',
  recording_closed: 'This walkthrough was closed while this recorder was away, so recording stopped. The tickets filed from it are on the board.',
  recording_pause_limit: `This walkthrough stayed paused for ${MAX_RECORDING_PAUSE_MS / 60_000} minutes, so it stopped on its own. Its video and ticket markers are saved.`,
  another_tab_recording: 'Another Toado tab is already recording. Finish it there, then try again.',
  active_recording_limit: 'You have reached your active recording limit. Finish or discard a walkthrough below, then try again.',
  recording_not_enabled: 'Walkthrough recording is not turned on for this workspace. Switch to a workspace that has it, or ask Toado support to turn it on.',
  recording_minutes_exhausted: 'This workspace has used its recording minutes for this month. They reset on the 1st, or a workspace owner can add more from Billing.',
  recording_storage_full: 'This workspace has used all of its recording storage. Older video is removed on its retention schedule, or a workspace owner can upgrade the plan.',
  server_cleanup_pending: 'Local data is gone. Server retention cleanup remains scheduled.',
  recording_unavailable: 'That server recording is no longer active. Discard this local recovery and start a new walkthrough.',
  unsupported_browser: 'This Chrome version cannot create the required WebM recording format.',
  local_cleanup_failed: 'Chrome could not clear this local recording. Keep this tab open and try Discard again.',
  settlement_persistence_failed: 'The recording is safe, but Chrome could not save an open ticket boundary for retry.',
  microphone_lost_and_settlement_persistence_failed: 'The microphone disconnected, and Chrome could not save the open ticket boundary for retry. The recorded video remains safe.',
};

/**
 * Mirror of the server cap, used only until the first list response lands.
 * The server is authoritative and sends its own limit with every list.
 */
const MAX_ACTIVE_RECORDING_SESSIONS = 5;

/**
 * Recordings this tab has already sent someone to review once.
 *
 * Module-level so it survives this page unmounting and remounting, which is
 * what happens every time the person walks to review and comes back. Recording
 * ids are unique per walkthrough, so this only ever grows by one per
 * walkthrough and never needs clearing.
 */
const HANDED_OFF_RECORDINGS = new Set<string>();

/** How often to re-evaluate whether something in flight has gone quiet. */
const STALL_RECHECK_MS = 30_000;

/**
 * How far the server's clock may be projected forward from the last response.
 *
 * Polling is every 8 seconds while anything is moving, so this only binds when
 * refetches are failing — exactly when the page has no business issuing a
 * verdict about the server's workers.
 */
const MAX_PROJECTED_AGE_MS = 2 * 60 * 1_000;

const BUSY_PHASES: RecorderPhase[] = [
  'requesting-display',
  'requesting-microphone',
  'starting',
  'recording',
  'stopping',
];

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function recoveryTitle(session: LocalRecordingSession): string {
  return session.status === 'interrupted' ? 'Interrupted walkthrough' : 'Saved walkthrough';
}

function backgroundUploadLabel(upload: LocalRecordingUpload): string {
  if (upload.status === 'ready') return 'Ready for ticket review';
  if (upload.status === 'failed') return 'Video needs attention';
  if (upload.status === 'processing') return 'Creating ticket clips';
  if (upload.status === 'cleanup') return 'Clearing local video';
  if (upload.status === 'uploading') return 'Uploading walkthrough';
  return 'Preparing walkthrough';
}

/**
 * Video recordings (TOADO-444): the recorder, and the ones already made, as two tabs with their own
 * addresses (`/recordings` and `/recordings/history`).
 */
export function RecordingsPage({ tab = 'recorder' }: { tab?: WalkthroughTab } = {}) {
  // This tab's title (TOADO-905).
  const tabWorkspace = useTabWorkspaceName();
  useTabTitle({ parts: [tabWorkspace, "Video recordings"] });
  const { user, currentCompanyId, features } = useAuth();
  const currentCompany = useCurrentCompany();
  const retentionDays = useVideoRetentionDays(currentCompanyId ?? undefined);
  /**
   * Whether this workspace lets a walkthrough start (TOADO-493). The page stays
   * open without it, because one started before the workspace lost access may
   * still need reviewing, but it does not offer a new one. Absent from an API
   * from before the setting, where reaching this page already meant yes.
   */
  const workspaceRecords = currentCompany?.features?.recording ?? features.v2Recorder;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    controller,
    snapshot,
    segmentController,
    segments,
    pipController,
    pip,
    uploadController,
    uploads,
  } = useRecorder();
  const [projectId, setProjectId] = useState('');
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [deviceLoading, setDeviceLoading] = useState(false);
  const controlPreview = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('control')
    : null;
  const previewController = useMemo(() => ({
    startSegment: async () => {},
    captureMoment: async () => {},
    finishSegment: async () => true,
    stopRecording: async () => {},
    dismissNotice: () => {},
  }) as unknown as RecordingSegmentController, []);
  const previewSegments = useMemo<RecordingSegmentSnapshot | null>(() => {
    if (!controlPreview) return null;
    const openSegment = controlPreview === 'idle' ? null : {
      id: 'seg-preview',
      sessionId: 'rec-preview',
      startMs: 12_000,
      segmentType: controlPreview === 'bug' ? 'bug' as const : 'feature_request' as const,
      snapshots: [],
      snapshotCount: 3,
    };
    return {
      phase: 'ready',
      recordingId: 'rec-preview',
      segments: openSegment ? [openSegment] : [],
      openSegment,
      pendingSettlements: 0,
      terminalSettlements: 0,
      cleanupSettlements: 0,
      notice: null,
    };
  }, [controlPreview]);

  const projectsQuery = useQuery({
    queryKey: ['projects', currentCompanyId, 'recorder'],
    queryFn: () => currentCompanyId ? api.listProjects(currentCompanyId) : Promise.resolve([]),
    enabled: Boolean(currentCompanyId),
  });
  const projects = projectsQuery.data ?? [];

  useEffect(() => {
    if (!projectId && projects[0]) setProjectId(projects[0].id);
    if (projectId && !projects.some((project) => project.id === projectId)) {
      setProjectId(projects[0]?.id ?? '');
    }
  }, [projectId, projects]);

  const refreshDevices = async () => {
    setDeviceLoading(true);
    try {
      // Drop entries with no deviceId. Chrome emits one such placeholder when
      // microphone permission has been denied, and keeping it renders a
      // nameless "Microphone 1" option whose value matches no selection —
      // the picker looks populated but nothing can be chosen.
      const microphones = (await controller.listMicrophones())
        .filter((device) => device.deviceId);
      setDevices(microphones);
      setDeviceId((current) => (microphones.some((device) => device.deviceId === current)
        ? current
        : microphones[0]?.deviceId ?? ''));
    } catch {
      setDevices([]);
      setDeviceId('');
    } finally {
      setDeviceLoading(false);
    }
  };

  useEffect(() => {
    void refreshDevices();
    const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!mediaDevices?.addEventListener) return;
    const onChange = () => { void refreshDevices(); };
    mediaDevices.addEventListener('devicechange', onChange);
    return () => mediaDevices.removeEventListener('devicechange', onChange);
    // Controller identity is stable for the provider lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller]);

  useEffect(() => () => controller.cancelMicrophoneTest(), [controller]);

  const selectedProject = projects.find((project) => project.id === projectId);
  const microphoneReady = snapshot.microphone.kind === 'ready'
    && snapshot.microphone.deviceId === deviceId;
  const busy = BUSY_PHASES.includes(snapshot.phase);
  const canStart = Boolean(
    user && currentCompanyId && workspaceRecords && projectId && deviceId && microphoneReady && !busy,
  );

  // Off until somebody turns it on, and off again on the history tab or once a
  // recording takes it: see useMicrophoneSwitch. It used to open the moment the
  // page loaded and stay open for as long as the page was showing.
  const microphoneSwitch = useMicrophoneSwitch({
    controller,
    microphone: snapshot.microphone,
    deviceId,
    busy,
    showing: tab === 'recorder',
  });
  const recoveries = useMemo(
    () => snapshot.recoveries.filter((session) => session.id !== snapshot.session?.id),
    [snapshot.recoveries, snapshot.session?.id],
  );

  // Finishing a walkthrough is destructive and irreversible: it closes the
  // session and starts the upload, so a mis-click throws away however long the
  // user has been narrating. Require a deliberate press-and-hold rather than a
  // single click. Reported after two accidental stops.
  const STOP_HOLD_MS = 800;
  const [stopHoldProgress, setStopHoldProgress] = useState(0);
  const stopHoldTimer = useRef<number | null>(null);
  const stopHoldStart = useRef(0);

  const clearStopHold = useCallback(() => {
    if (stopHoldTimer.current !== null) {
      cancelAnimationFrame(stopHoldTimer.current);
      stopHoldTimer.current = null;
    }
    setStopHoldProgress(0);
  }, []);

  const beginStopHold = useCallback(() => {
    if (stopHoldTimer.current !== null) return;
    stopHoldStart.current = performance.now();
    const tick = () => {
      const held = performance.now() - stopHoldStart.current;
      const ratio = Math.min(1, held / STOP_HOLD_MS);
      setStopHoldProgress(ratio);
      if (ratio >= 1) {
        stopHoldTimer.current = null;
        setStopHoldProgress(0);
        void segmentController.stopRecording();
        return;
      }
      stopHoldTimer.current = requestAnimationFrame(tick);
    };
    stopHoldTimer.current = requestAnimationFrame(tick);
  }, [segmentController]);

  // A hold in progress must not survive the component unmounting, or the
  // callback fires against a torn-down controller.
  useEffect(() => clearStopHold, [clearStopHold]);
  const currentUpload = uploads.uploads.find(
    (upload) => upload.recordingId === snapshot.session?.id,
  );
  const progress = deriveRecordingProgress(currentUpload);

  // Hand the user to the review page once the walkthrough is ready to act on,
  // instead of leaving them on a "saved" screen to discover the link
  // themselves (TOADO-303).
  //
  // The "already sent" memory is module-level, not a ref, because navigating
  // to review unmounts this page while `RecorderProvider` (mounted above the
  // router) keeps the completed session. A ref would be reconstructed empty on
  // the way back and fire again, so "Record another" would bounce straight to
  // review and there would be no way back to the recorder at all.
  useEffect(() => {
    const recordingId = snapshot.session?.id;
    if (!recordingId || snapshot.phase !== 'completed') return;
    if (!shouldHandOffToReview(progress)) return;
    if (HANDED_OFF_RECORDINGS.has(recordingId)) return;
    HANDED_OFF_RECORDINGS.add(recordingId);
    navigate(`/recordings/${recordingId}/review`);
  }, [navigate, progress, snapshot.phase, snapshot.session?.id]);

  // Server truth about everything still open, so a walkthrough left processing
  // on another machine (or one whose local record was cleared) still has a
  // trace here (TOADO-306). Polling is cheap and stops once nothing is moving.
  const inFlightQuery = useQuery({
    queryKey: ['recordings', 'in-flight', user?.id ?? 'anonymous'],
    // Active-only: the unfiltered list is capped at the newest 50 rows, so
    // committed history can push a genuinely stuck session off the end of the
    // very list that exists to surface it.
    queryFn: ({ signal }) => api.listRecordings({ activeOnly: true }, signal),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const moving = items.some((item) => item.status === 'recording' || item.status === 'processing');
      return moving ? 8_000 : false;
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(user?.id),
  });

  // A stall verdict is a function of elapsed time, not of any prop, so nothing
  // in the memo below changes as the clock passes the threshold. TanStack's
  // structural sharing keeps the response object identical across polls of an
  // unchanging session, which is exactly the dead-job case, so without this
  // tick a stuck walkthrough would sit on "Cutting ticket clips" forever —
  // the failure this ticket exists to end.
  const [clockTick, setClockTick] = useState(0);
  const anythingUnsettled = (inFlightQuery.data?.items ?? []).some(
    (item) => item.status === 'recording' || item.status === 'processing',
  );
  useEffect(() => {
    if (!anythingUnsettled) return;
    const timer = window.setInterval(() => setClockTick((value) => value + 1), STALL_RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [anythingUnsettled]);

  const backgroundUploads = useMemo(
    () => uploads.uploads.filter((upload) => upload.recordingId !== snapshot.session?.id),
    [snapshot.session?.id, uploads.uploads],
  );

  // The session being recorded right now already owns the panel above, so it is
  // hidden from the cards. It is NOT removed from the slot count: it holds a
  // slot, and subtracting it would show a free slot that the next start
  // request refuses.
  const inFlight = useMemo(() => {
    const data = inFlightQuery.data;
    // Measure elapsed time against the server's clock, carried in the same
    // response as the timestamps. Comparing a server timestamp to this
    // machine's clock made the stall verdict a function of how well the
    // laptop's time is set: twenty minutes fast declared every healthy session
    // dead on load, and slow hid genuine stalls by the skew.
    const serverNow = data?.now ? new Date(data.now) : undefined;
    const clockValid = serverNow !== undefined && Number.isFinite(serverNow.getTime());
    // Ageing between polls is measured forward from when the response arrived,
    // so a page left open still crosses the threshold on its own.
    //
    // Capped, because unbounded projection turns an unreachable server into a
    // verdict about a job it cannot see. During an outage the response freezes
    // while the projected clock runs on, and every healthy session eventually
    // "stalls" in projection alone — the page accusing a server it cannot
    // reach. Past the cap the honest answer is that we do not know, which is
    // what a frozen clock says.
    const rawAge = clockValid && inFlightQuery.dataUpdatedAt
      ? Date.now() - inFlightQuery.dataUpdatedAt
      : 0;
    const referenceAge = Math.min(Math.max(0, rawAge), MAX_PROJECTED_AGE_MS);
    // Staleness means "the last attempt to check failed", not "the last
    // response is old". Age alone conflates a broken connection with polling
    // that has legitimately stopped because nothing is moving, and on a quiet
    // page that produced a banner asserting the customer's network was down.
    // Requiring an actual failed attempt as well as age says only what is
    // known.
    const stale = inFlightQuery.failureCount > 0 && rawAge > MAX_PROJECTED_AGE_MS;
    return summarizeInFlight(data?.items ?? [], backgroundUploads, {
      ...(data ? { activeCount: data.activeCount } : {}),
      activeLimit: data?.activeLimit ?? MAX_ACTIVE_RECORDING_SESSIONS,
      // A recording still in progress definitely holds a slot, so it is
      // vouched for. Once it completes we cannot tell from here whether it has
      // since been committed, so we hide it without claiming its slot rather
      // than absorb someone else's.
      ...(snapshot.session?.id
        ? { hide: [{ id: snapshot.session.id, holdsSlot: snapshot.phase !== 'completed' }] }
        : {}),
      stale,
      now: clockValid ? new Date(serverNow.getTime() + referenceAge) : new Date(),
    });
    // `clockTick` is a dependency on purpose: staleness is a function of the
    // clock, and nothing else here changes when a job quietly dies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundUploads, clockTick, inFlightQuery.data, inFlightQuery.dataUpdatedAt, inFlightQuery.failureCount, snapshot.session?.id]);

  const uploadsByRecordingId = useMemo(
    () => new Map(backgroundUploads.map((upload) => [upload.recordingId, upload])),
    [backgroundUploads],
  );

  // Both of these change server state the list is reporting on. Polling stops
  // once nothing is moving, so without an explicit refetch a discarded session
  // keeps its card and keeps its slot marked occupied until some unrelated
  // trigger happens to refresh the query.
  const refreshInFlight = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['recordings', 'in-flight'] }),
    [queryClient],
  );
  const discardInFlight = useCallback(async (recordingId: string) => {
    await controller.discard(recordingId);
    await refreshInFlight();
  }, [controller, refreshInFlight]);
  const retryInFlight = useCallback(async (recordingId: string) => {
    await uploadController.retry(recordingId);
    await refreshInFlight();
  }, [refreshInFlight, uploadController]);

  // Start and Continue show the recording guide first, unless this person
  // chose not to see it (TOADO-969). It keeps what was asked for, not how: Got
  // it runs the start as this render has it, with this render's checks (the
  // microphone, the workspace, the walkthrough may all have changed while it
  // was open), from its own click, so Chrome keeps the gesture. A start keeps
  // where it was asked to record: a different workspace or project by the time
  // Got it is pressed starts nothing, rather than recording somewhere else.
  type GuideIntent =
    | { kind: 'start'; companyId: string | null; projectId: string }
    | { kind: 'resume'; sessionId: string };
  const [guideHidden, setGuideHidden] = useRecordingGuideHidden(user?.id);
  const [guideFor, setGuideFor] = useState<{ intent: GuideIntent; opener: HTMLElement | null } | null>(null);
  // Why a start from the guide did not happen, said where the person can read it.
  const [guideProblem, setGuideProblem] = useState<string | null>(null);
  const guideProblemRef = useRef<HTMLParagraphElement>(null);
  const pageHeadingRef = useRef<HTMLHeadingElement>(null);
  /** Runs what was asked for, if it still can be; otherwise says why not. */
  const runIntent = (intent: GuideIntent): string | null => {
    if (intent.kind === 'start') {
      if (intent.companyId !== (currentCompanyId ?? null) || intent.projectId !== projectId) {
        return 'Nothing was recorded: the workspace or project changed while the guide was open. Check it, then press Start walkthrough again.';
      }
      if (!canStart) return 'Nothing was recorded: Start walkthrough is not ready. Check it, then press it again.';
      // A null here means it started: start() says if it did not.
      return start() ? null : 'Nothing was recorded: Start walkthrough is not ready. Check it, then press it again.';
    }
    const session = recoveries.find((candidate) => candidate.id === intent.sessionId);
    if (!session || session.status !== 'interrupted') return 'Nothing was recorded: that walkthrough can no longer be continued.';
    // The causes named for their own words; resume() says if it still did not.
    if (!user) return 'Nothing was recorded: you are signed out. Sign in, then press Continue again.';
    if (busy) return 'Nothing was recorded: the recorder is still finishing. Press Continue again in a moment.';
    if (!microphoneReady) return 'Nothing was recorded: turn your mic on, then press Continue again.';
    return resume(session) ? null : 'Nothing was recorded: that walkthrough could not be continued. Press Continue again.';
  };
  const throughGuide = (intent: GuideIntent, opener: HTMLElement | null) => {
    setGuideProblem(null);
    if (guideHidden) {
      // Its checks match the buttons' disabled state today; said anyway if not.
      const problem = runIntent(intent);
      if (problem) setGuideProblem(problem);
      return;
    }
    // What was pressed, so a guide closed without starting gives focus back to
    // it. Taken from the click, not from focus: Safari does not focus a button
    // it was clicked on.
    setGuideFor({ intent, opener });
  };
  /**
   * Where focus goes once the guide has gone: to the sentence saying why
   * nothing started, or back to what opened it if that can still take it.
   */
  const returnFocus = (opener: HTMLElement | null, explain: boolean) => {
    window.setTimeout(() => {
      if (explain) {
        guideProblemRef.current?.focus();
        return;
      }
      if (opener?.isConnected && !(opener as HTMLButtonElement).disabled) {
        opener.focus();
        return;
      }
      // An opener disabled while the guide was open: when the mic went off, the
      // hint named by its `data-guide-hint` says why (Start's for Start,
      // Continue's for Continue), and focus goes there. For any other reason,
      // or an opener gone, the page's own heading, rather than nowhere.
      const hint = opener?.dataset.guideHint ? document.querySelector<HTMLElement>(`[data-guide-fallback="${opener.dataset.guideHint}"]`) : null;
      if (hint) hint.focus();
      else pageHeadingRef.current?.focus();
    }, 0);
  };
  const requestStart = (event: React.MouseEvent<HTMLElement>) => {
    if (!canStart) return;
    throughGuide({ kind: 'start', companyId: currentCompanyId ?? null, projectId }, event.currentTarget);
  };

  /** Starts a walkthrough, and says whether it did. */
  const start = (): boolean => {
    if (!user || !currentCompanyId || !canStart) return false;
    startRecorderWithFloatingControl(controller, pipController, {
      ownerId: user.id,
      companyId: currentCompanyId,
      projectId,
      microphoneDeviceId: deviceId,
    });
    return true;
  };

  /** Continues a walkthrough, and says whether it did. */
  const resume = (session: LocalRecordingSession): boolean => {
    if (!user || !microphoneReady) return false;
    startRecorderWithFloatingControl(controller, pipController, {
      ownerId: user.id,
      companyId: session.companyId,
      projectId: session.projectId,
      microphoneDeviceId: deviceId,
      resume: session,
    });
    return true;
  };

  const phaseLabel = snapshot.phase === 'requesting-display'
    ? 'Choose what to share in Chrome'
    : snapshot.phase === 'requesting-microphone'
      ? 'Connecting your microphone'
      : snapshot.phase === 'starting'
        ? 'Preparing secure local storage'
        : snapshot.phase === 'stopping'
          ? 'Saving the final chunk'
          : 'Start walkthrough';

  // Whether this person has the extension connected: the top bar's own signal (GetExtensionPrompt).
  const extensionStatus = useQuery({
    queryKey: ['extension-status'],
    queryFn: () => api.getExtensionStatus(),
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    retry: 1,
  });

  return (
    <main className={`${styles.page} ${pageBackground}`}>
      {/* The shared page header (Jason, 16 Oct); its line says what each tab is for. */}
      <PageHeader icon={<Clapperboard size={18} />} title="Video recordings" testId="walkthrough-header" headingRef={pageHeadingRef}
        subtitle={tab === 'recorder'
          ? 'Share the product, explain what you see, and keep every moment ready for ticket review.'
          : 'Every walkthrough you have finished. Open one to see its tickets, write-ups and screenshots.'} />
      <div className={styles.body}>
      {/* Recording one, and the ones already made (Jason, 24 Sep). */}
      <div className={styles.tabs}>
        <WalkthroughTabs active={tab} />
      </div>

      <div className={styles.srOnly} aria-live="polite" aria-atomic="true">
        {snapshot.phase === 'recording'
          ? 'Walkthrough recording started.'
          : snapshot.phase === 'stopping'
            ? 'Recording stopped. Saving the final chunk.'
            : snapshot.phase === 'completed'
              ? 'Walkthrough saved locally.'
              : snapshot.phase === 'error'
                ? 'The recorder needs attention.'
                : ''}
      </div>

      <div role="tabpanel" id={WALKTHROUGH_PANEL_ID} aria-labelledby={`walkthrough-tab-${tab}`}>
      {tab === 'history' ? (
        currentCompanyId && (
          // Keyed by workspace, so switching one starts the list again at page 1.
          <PastWalkthroughs key={currentCompanyId} companyId={currentCompanyId} projects={projects} />
        )
      ) : (
      <>
      {snapshot.errorCode && (
        <div className={styles.notice} role="alert">
          <span>{ERROR_MESSAGES[snapshot.errorCode] ?? 'The recorder needs attention before you continue.'}</span>
          {snapshot.phase === 'error' && (
            <button type="button" onClick={() => controller.reset()}>Dismiss</button>
          )}
        </div>
      )}

      {/* The extension records walkthroughs: when this person has none connected, say so first (Jason, 16 Oct). */}
      {extensionStatus.data?.installed === false && <ExtensionNeeded wide />}

      {/* Full width with a right panel, as Dashboard and Reports (Jason, 16 Oct). */}
      <section className={styles.workspace} aria-label="Walkthrough recorder setup">
        <div className={styles.mainColumn}>
        <div className={styles.mainCard}>
          <div className={styles.cardHeading}>
            <div className={styles.cardIcon}><Clapperboard size={22} /></div>
            <div>
              <span>New walkthrough</span>
              <h2>Ready when you are</h2>
            </div>
          </div>

          {snapshot.phase === 'recording' || snapshot.phase === 'stopping' ? (
            <div className={styles.recordingState}>
              <div className={styles.liveBadge}><span /> Recording</div>
              <div className={styles.timer}>{formatRecorderDuration(snapshot.elapsedMs)}</div>
              <p>{selectedProject?.name ?? 'Selected project'}</p>
              <div className={styles.recordingStats}>
                <span><Database size={15} /> {snapshot.session?.chunkCount ?? 0} chunks</span>
                <span>{formatBytes(snapshot.session?.storedBytes ?? 0)} saved locally</span>
              </div>
              <div className={styles.pipLaunch} data-state={pip.status}>
                <div className={styles.pipLaunchIcon}><PictureInPicture2 size={18} /></div>
                <div>
                  <strong>
                    {pip.status === 'open'
                      ? 'Floating controls are ready'
                      : pip.reason === 'user_closed'
                        ? 'Floating controls were closed'
                        : pip.status === 'unsupported'
                          ? 'Using controls in this tab'
                          : 'Keep controls over the app'}
                  </strong>
                  <small>
                    {pip.status === 'open'
                      ? `${segments.openSegment ? 'A ticket is open' : 'Start a ticket'} in the always-on-top window.`
                      : pip.status === 'unsupported'
                        ? 'Switch back here to mark ticket boundaries while you narrate.'
                        : 'Open the always-on-top panel before switching to the app you are reviewing.'}
                  </small>
                </div>
                {pip.status === 'open' ? (
                  <button type="button" onClick={() => pipController.close()}>Use here</button>
                ) : pip.status !== 'unsupported' ? (
                  <button
                    type="button"
                    disabled={pip.status === 'opening' || snapshot.phase === 'stopping'}
                    onClick={() => { void pipController.open(); }}
                  >
                    {pip.status === 'opening' ? 'Opening' : pip.status === 'failed' ? 'Try again' : 'Open panel'}
                  </button>
                ) : null}
              </div>
              <button
                className={styles.stopButton}
                type="button"
                disabled={snapshot.phase === 'stopping'}
                style={{ '--hold': `${Math.round(stopHoldProgress * 100)}%` } as React.CSSProperties}
                data-holding={stopHoldProgress > 0 ? 'true' : undefined}
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  event.currentTarget.setPointerCapture(event.pointerId);
                  beginStopHold();
                }}
                onPointerUp={clearStopHold}
                onPointerCancel={clearStopHold}
                onPointerLeave={clearStopHold}
                // Keyboard equivalent: Space/Enter held down repeats keydown,
                // so the same progress fills and a single tap does nothing.
                onKeyDown={(event) => {
                  if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); beginStopHold(); }
                }}
                onKeyUp={clearStopHold}
                onBlur={clearStopHold}
              >
                <CircleStop size={18} />
                {snapshot.phase === 'stopping'
                  ? 'Saving final chunk'
                  : stopHoldProgress > 0 ? 'Keep holding to finish' : 'Hold to finish walkthrough'}
              </button>
            </div>
          ) : snapshot.phase === 'completed' && snapshot.session ? (
            <div className={styles.completedState}>
              <div className={styles.completedIcon}><Check size={27} /></div>
              <h2>Walkthrough saved</h2>
              <p>{formatRecorderDuration(snapshot.elapsedMs)} across {snapshot.session.chunkCount} local chunks</p>
              <div
                className={styles.uploadState}
                data-status={progress.stage}
                role={progress.stage === 'failed' ? 'alert' : 'status'}
              >
                <div className={styles.uploadStateIcon}>
                  {progress.stage === 'failed' ? <TriangleAlert size={19} /> : <CloudUpload size={19} />}
                </div>
                <div>
                  <strong>{progress.title}</strong>
                  <small>{progress.detail}</small>
                  {progress.percent !== undefined && progress.stage === 'uploading' && (
                    <div
                      className={styles.uploadProgress}
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={progress.percent}
                      aria-label={`Upload ${progress.percent}% complete`}
                    >
                      <span style={{ width: `${progress.percent}%` }} />
                    </div>
                  )}
                </div>
                {progress.stage === 'failed' && currentUpload && (
                  <button type="button" onClick={() => uploadController.retry(currentUpload.recordingId)}>
                    <RefreshCw size={14} /> Retry
                  </button>
                )}
              </div>
              <div className={styles.completedActions}>
                {progress.actionable && (
                  <Link className={styles.reviewButton} to={`/recordings/${snapshot.session.id}/review`}>
                    <Play size={16} /> Review tickets
                  </Link>
                )}
                <button type="button" className={styles.secondaryButton} onClick={() => controller.reset()}>
                  <RotateCcw size={16} /> Record another
                </button>
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={() => { if (confirmDiscardWalkthrough()) void controller.discard(snapshot.session!.id); }}
                >
                  <Trash2 size={16} /> Discard
                </button>
              </div>
              {/* It said the video was kept for a seven-day recovery window. A finished
                  walkthrough's video is kept far longer than that on a paid plan; the
                  rules are spelled out under Past walkthroughs (TOADO-439). */}
              <small>Its video is kept {keptFor(retentionDays, 'for as long as your plan keeps walkthrough video')}{retentionDays ? ' after it is recorded' : ''}.</small>
            </div>
          ) : (
            <div className={styles.setup}>
              {!workspaceRecords && (
                <p className={styles.notice} role="status">
                  Walkthrough recording is not turned on for {currentCompany?.name ?? 'this workspace'}.
                  Switch to a workspace that has it, or ask Toado support to turn it on.
                </p>
              )}
              <label className={styles.field}>
                <span><b>1</b> Send tickets to</span>
                <select
                  value={projectId}
                  onChange={(event) => setProjectId(event.target.value)}
                  disabled={projectsQuery.isLoading || busy}
                >
                  {projects.length === 0 && <option value="">No writable projects</option>}
                  {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </label>

              <div className={styles.field}>
                <span><b>2</b> Check your microphone</span>
                <div className={styles.deviceRow}>
                  <select
                    aria-label="Microphone"
                    value={deviceId}
                    onChange={(event) => {
                      controller.cancelMicrophoneTest();
                      setDeviceId(event.target.value);
                    }}
                    disabled={deviceLoading || busy}
                  >
                    {devices.length === 0 && (
                      <option value="">
                        {deviceLoading ? 'Looking for microphones…' : 'Allow microphone access to choose an input'}
                      </option>
                    )}
                    {devices.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${index + 1}`}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label="Refresh microphones"
                    disabled={deviceLoading || busy}
                    onClick={() => { void refreshDevices(); }}
                  >
                    <RefreshCw size={16} className={deviceLoading ? styles.spinning : undefined} />
                  </button>
                </div>
                <div className={styles.micCheck}>
                  <div className={styles.meterIcon}><Mic2 size={17} /></div>
                  <div
                    className={styles.meterTrack}
                    aria-label="Microphone input level"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(snapshot.microphone.level * 100)}
                  >
                    <div style={{ width: snapshot.microphone.level === 0 ? '0%' : `${Math.max(2, snapshot.microphone.level * 100)}%` }} />
                  </div>
                  <button
                    type="button"
                    aria-pressed={microphoneReady}
                    disabled={!deviceId || snapshot.microphone.kind === 'requesting' || busy}
                    onClick={microphoneSwitch.toggle}
                  >
                    {snapshot.microphone.kind === 'requesting'
                      ? 'Connecting'
                      : microphoneReady ? 'Turn mic off' : 'Turn mic on'}
                  </button>
                </div>
                <small>
                  {microphoneReady
                    ? 'Speak normally. The purple meter should move before you start.'
                    : 'Your mic is off. Turn it on to check it before you start.'}
                </small>
              </div>

              <p className={styles.startHint}>
                Avoid saying passwords or other secrets. Recognizable tokens are masked in transcripts,
                but ordinary spoken secrets and the recording audio are not redacted. If transcription
                is requested, audio is sent to OpenAI before text redaction. To remove retained audio,
                permanently delete every ticket that references the recording.
              </p>
              <button className={styles.startButton} type="button" disabled={!canStart} onClick={requestStart} data-guide-hint="start">
                <MonitorUp size={19} /> {phaseLabel} <ChevronRight size={17} />
              </button>
              {guideHidden && (
                <button type="button" className={styles.guideRestore} onClick={() => setGuideHidden(false)}>
                  Show the start guide again
                </button>
              )}
              {workspaceRecords && !microphoneReady && <p className={styles.startHint} data-guide-fallback="start" tabIndex={-1}>Turn your mic on to start a walkthrough.</p>}
            </div>
          )}
        </div>

      <RecordingStartGuide
        open={guideFor !== null}
        onCancel={() => {
          const opener = guideFor?.opener ?? null;
          setGuideFor(null);
          // After the dialog has gone, or its own focus handling has the last word.
          returnFocus(opener, false);
        }}
        onStart={(dontShowAgain) => {
          const asked = guideFor;
          setGuideFor(null);
          if (dontShowAgain) setGuideHidden(true);
          const problem = asked ? runIntent(asked.intent) : null;
          if (problem) {
            setGuideProblem(problem);
            returnFocus(asked?.opener ?? null, true);
          }
        }}
      />
      {guideProblem && (
        <p ref={guideProblemRef} className={styles.notice} role="status" tabIndex={-1}>{guideProblem}</p>
      )}

      {recoveries.length > 0 && (
        <section className={styles.recoverySection} aria-labelledby="recovery-title">
          <div>
            <h2 id="recovery-title">Continue where you left off</h2>
            {/* Continue records, so it needs the mic, which is off until turned on. */}
            {!microphoneReady && recoveries.some((session) => session.status === 'interrupted') && (
              <p className={styles.startHint} data-guide-fallback="continue" tabIndex={-1}>Turn your mic on to continue an interrupted walkthrough.</p>
            )}
          </div>
          <div className={styles.recoveryGrid}>
            {recoveries.map((session) => (
              <article key={session.id} className={styles.recoveryCard}>
                <div className={styles.recoveryIcon}><Play size={18} /></div>
                <div className={styles.recoveryCopy}>
                  <strong>{recoveryTitle(session)}</strong>
                  <span>{formatRecorderDuration(session.elapsedMs)} · {session.chunkCount} chunks · {formatBytes(session.storedBytes)}</span>
                  <small><When at={session.updatedAt} prefix="Saved " /></small>
                </div>
                <div className={styles.recoveryActions}>
                  {session.status === 'interrupted' && (
                    <button type="button" data-guide-hint="continue" disabled={!microphoneReady || busy} onClick={(event) => throughGuide({ kind: 'resume', sessionId: session.id }, event.currentTarget)}>
                      <Play size={15} /> Continue
                    </button>
                  )}
                  {session.status === 'interrupted' && (
                    <button type="button" onClick={() => { void controller.keepForProcessing(session.id); }}>
                      Keep
                    </button>
                  )}
                  <button className={styles.trashButton} type="button" aria-label="Discard recovery" onClick={() => { if (confirmDiscardWalkthrough()) void controller.discard(session.id); }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
        </div>

        <div className={styles.sideColumn}>
        <LearnAboutRecordings retentionDays={retentionDays} />
        <aside className={styles.sideCard}>
          <div className={styles.sideTitle}>
            <Volume2 size={18} />
            <div><strong>What gets saved</strong><span>Private to your account</span></div>
          </div>
          <ol className={styles.steps}>
            <li><span>1</span><div><strong>Chrome screen share</strong><small>You choose a tab, window, or screen.</small></div></li>
            <li><span>2</span><div><strong>Your selected microphone</strong><small>Voice stays aligned with the video.</small></div></li>
            <li><span>3</span><div><strong>Five-second local chunks</strong><small>Reload-safe storage without growing tab memory.</small></div></li>
          </ol>
          <div className={styles.localOnly}>
            {currentUpload && currentUpload.status !== 'ready'
              ? <><CloudUpload size={15} /> Background upload is active</>
              : snapshot.phase === 'completed'
                ? <><Check size={15} /> Recording is safely handed off</>
                : <><Database size={15} /> Upload starts after Stop</>}
          </div>
        </aside>
        {/* When whether it is connected could not be told, the reminder sits here, quieter. */}
        {extensionStatus.isError && <ExtensionNeeded />}
        {/* Walkthroughs not finished, in the right panel (Jason, 16 Oct). It is always
            here, so what a session slot is can be read before one is taken. */}
        <section className={styles.unfinished} aria-labelledby="background-upload-title" data-testid="unfinished-walkthroughs">
          <div className={styles.unfinishedHeading}>
            <h2 id="background-upload-title">Walkthroughs you have not finished</h2>
            <InfoTip label="walkthroughs you have not finished">
              A walkthrough you start holds one of your {inFlight.activeLimit} session slots while it records, uploads and
              waits for review. Filing its tickets or discarding it frees the slot. When every slot is taken, finish or
              discard one before starting another. Open one to see how its upload is going and to review it.
            </InfoTip>
          </div>
          {/* Only when the server has answered. Before that the count is a
              local guess, and asserting "5 of 5" from stale local records
              would announce a limit the server never reported. */}
          {inFlight.activeCountKnown && (
            <small className={styles.slotCount} data-at-limit={inFlight.atLimit ? 'true' : undefined}>
              {inFlight.activeCount} of {inFlight.activeLimit} session slots in use
            </small>
          )}

          {inFlight.stale && (
            <p className={styles.slotNotice} role="status">
              These are the last states the server reported. The connection to
              it is not working right now, so nothing below is being checked.
            </p>
          )}
          {inFlight.atLimit && (
            <p className={styles.slotNotice} role="status">
              Every session slot is taken. Commit or discard one of these before starting another walkthrough.
            </p>
          )}
          {inFlight.hiddenSlots > 0 && (
            <p className={styles.slotNotice}>
              {inFlight.hiddenSlots} {inFlight.hiddenSlots === 1 ? 'slot is' : 'slots are'} held by
              {' '}walkthroughs in projects you can no longer open. Ask an owner to restore access, or wait for
              {' '}them to expire.
            </p>
          )}

          {inFlight.sessions.length === 0 ? (
            <p className={styles.unfinishedEmpty}>Nothing waiting. A walkthrough you stop stays here until its tickets are filed.</p>
          ) : (
          <div className={styles.backgroundGrid}>
            {inFlight.sessions.map((item) => {
              const upload = uploadsByRecordingId.get(item.recordingId);
              return (
                <article
                  key={item.recordingId}
                  className={styles.backgroundCard}
                  data-status={item.stalled ? 'failed' : item.stage}
                >
                  <div className={styles.backgroundIcon}>
                    {item.stalled || item.stage === 'failed'
                      ? <TriangleAlert size={18} />
                      : item.stage === 'ready' ? <Check size={18} /> : <CloudUpload size={18} />}
                  </div>
                  <div className={styles.backgroundCopy}>
                    <strong>{item.title}</strong>
                    <span>
                      {item.percent === undefined ? '' : `${item.percent}% handed off · `}
                      {item.updatedAt ? <When at={item.updatedAt} prefix="last change " /> : 'no server activity yet'}
                    </span>
                    <span>{item.detail}</span>
                    {item.percent !== undefined && (item.stage === 'uploading' || item.stage === 'recording') && (
                      <div
                        className={styles.uploadProgress}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={item.percent}
                        aria-label={`Upload ${item.percent}% complete`}
                      >
                        <span style={{ width: `${item.percent}%` }} />
                      </div>
                    )}
                    {upload?.lastErrorCode && <small>Error code: {upload.lastErrorCode}</small>}
                  </div>
                  <div className={styles.backgroundActions}>
                    {/* Every entry links to its review page, including ones still
                        working: that page is where retries and per-segment state
                        live, and a dead end here is what TOADO-306 is about. */}
                    <Link to={`/recordings/${item.recordingId}/review`}>
                      <Play size={14} /> {item.stage === 'ready' ? 'Review' : 'Open'}
                    </Link>
                    {upload?.status === 'failed' && (
                      <button type="button" onClick={() => { void retryInFlight(item.recordingId); }}>
                        <RefreshCw size={14} /> Retry
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.trashButton}
                      aria-label="Discard this walkthrough"
                      onClick={() => { if (confirmDiscardWalkthrough()) void discardInFlight(item.recordingId); }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          )}
        </section>
        </div>
      </section>

      </>
      )}
      </div>

      {previewSegments && (
        <RecorderFloatingControl
          controller={previewController}
          snapshot={previewSegments}
          elapsedMs={82_000}
          stopping={false}
        />
      )}
      </div>
    </main>
  );
}

/**
 * Walkthroughs are recorded with the extension (Jason, 16 Oct): what it is
 * for, and the way to get it. `wide` is the box across the top of the page,
 * for someone with no extension connected; without it, the quieter one under
 * What gets saved, for when that could not be told.
 */
function ExtensionNeeded({ wide = false }: { wide?: boolean }) {
  return (
    <div className={wide ? styles.extensionWide : styles.extensionSide} data-testid={wide ? 'extension-needed' : 'extension-reminder'} role={wide ? 'status' : undefined}>
      <span className={styles.extensionIcon} aria-hidden="true"><Puzzle size={18} /></span>
      <div className={styles.extensionText}>
        <strong>{wide ? 'Install the Toado extension to record' : 'Walkthroughs need the Toado extension'}</strong>
        <span>
          {wide
            ? 'Walkthroughs are recorded with the Chrome extension: it shares your screen, places your pins and keeps each page\'s DevTools data. Install it and connect it to your account, then come back here.'
            : 'Install it and connect it to your account before you record.'}
        </span>
      </div>
      <Link to={EXTENSION_INSTALL_PATH} className={styles.extensionButton}>Get the extension</Link>
    </div>
  );
}
